import { Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import AddToBasketConfirmationDialog from '@/components/AddToBasketConfirmationDialog/AddToBasketConfirmationDialog';
import CardGrid from '@/components/CardGrid/CardGrid';
import ContactFormDialog from '@/components/ContactFormDialog/ContactFormDialog';
import getConfig from '@/configs/global';
import {
  priceCatalogProductsPropType,
  priceCatalogRequestTypePropType,
} from '@/constants/propTypes';
import {
  RENTAL_PRICE_BASIS,
  RENTAL_PRICE_BASIS_TRANSLATION_KEYS,
} from '@/constants/rentalPrice';
import { PRICE_CATALOG_REQUEST_TYPES } from '@/helpers/constants';
import { useBasketContext } from '@/hooks/useBasketContext';
import useFormatMessage from '@/hooks/useFormatMessage';
import useFormatPrice from '@/hooks/useFormatPrice';
import useFormatProductAttributeValue from '@/hooks/useFormatProductAttributeValue';
import { useGenericContactFormContext } from '@/hooks/useGenericContactFormContext';

const {
  publicRuntimeConfig: { FEAT_FLAG_ATTRIBUTE_RANGES },
} = getConfig();

/**
 * PriceCatalog.
 */
const PriceCatalog = ({
  products,
  'data-cy': dataCy,
  productGroupName: parentProductGroupName,
  productGroupId: parentProductGroupId,
  loading,
  requestType,
}) => {
  const { addRequestToBasket, machineAlreadyInBasket } = useBasketContext();
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const {
    contactFormData,
    resetContactFormData,
    updateContactFormFieldData,
  } = useGenericContactFormContext();
  const router = useRouter();
  const formatMessage = useFormatMessage();
  const formatPrice = useFormatPrice();
  const formatValue = useFormatProductAttributeValue();

  const [duplicatedMachineData, setDuplicatedMachineData] = useState(null);

  const showContactForm =
    requestType === PRICE_CATALOG_REQUEST_TYPES.PURCHASE_REQUEST;

  const getMessageBody = productName =>
    formatMessage('pricecatalog_email_body', {
      productName,
    });

  const handleOpenContactForm = ({ title }) => {
    setContactFormOpen(true);
    updateContactFormFieldData('message', getMessageBody(title));
  };

  const handleCloseContactForm = () => setContactFormOpen(false);

  const handleRentalRequestClick = (
    event,
    {
      title,
      productReferenceId,
      salesforceId,
      automatedOffer,
      sendAutomatedOffer,
      minLeadTime,
      rentalPriceBasis,
      rentalPrice,
      priceBasis,
      productGroupName,
      productGroupId,
    }
  ) => {
    const request = {
      rentalObject: {
        name: title,
        productReferenceId,
        salesforceId,
        productGroupName: productGroupName ?? parentProductGroupName,
        productGroupId: productGroupId ?? parentProductGroupId,
        automatedOffer,
        sendAutomatedOffer,
        minLeadTime,
        rentalPriceBasis,
        rentalPrice,
        invoiceBasis: priceBasis,
      },
      count: 1,
      rentalHours: null,
    };

    if (machineAlreadyInBasket(request.rentalObject)) {
      setDuplicatedMachineData(request);
    } else {
      handleAddToBasket(request);
    }
  };

  const handleAddToBasket = request => {
    addRequestToBasket(request);
    router.push('/basket');
  };

  const getPriceText = (price, priceBasis) => {
    if (!price) return '';
    if (priceBasis === RENTAL_PRICE_BASIS.UNIT) {
      return `<span data-cy="price">${formatMessage('rental_price_unit', {
        price: formatPrice(price),
      })}</span>`;
    } else {
      return `<span data-cy="price">${formatMessage('rental_price_from', {
        price: formatPrice(price),
      })}</span> <span data-cy="priceBasis">${formatMessage(
        priceBasis
          ? RENTAL_PRICE_BASIS_TRANSLATION_KEYS[priceBasis]
          : 'rental_price_basis_day'
      )}</span>`;
    }
  };

  const getSubtitle = ({ initialPrice, price, priceBasis }) => {
    const initialPriceText = initialPrice
      ? formatMessage('rental_price_from', {
          price: formatPrice(initialPrice),
        })
      : '';

    const priceText = !price
      ? formatMessage('rental_price_placeholder')
      : getPriceText(price, priceBasis);

    if (initialPriceText) {
      return (
        <Stack
          direction="row"
          alignItems="baseline"
          flexWrap="wrap"
          columnGap={1}
          data-cy="subtitle">
          <Typography
            variant="body1Redesign"
            component="div"
            color="grayscale.500"
            sx={{ textDecoration: 'line-through' }}
            dangerouslySetInnerHTML={{ __html: initialPriceText }}
            data-cy="initialPrice"
          />
          {priceText && (
            <Typography
              variant="h6Redesign"
              component="div"
              color="primary.main"
              dangerouslySetInnerHTML={{ __html: priceText }}
            />
          )}
        </Stack>
      );
    }

    return priceText;
  };

  const getCardAreaLink = ({ url, showContactForm, productReferenceId }) => {
    if (url && showContactForm) {
      return url;
    }
    if (productReferenceId) {
      return `${
        router.asPath.split('?')[0]
      }?productReferenceId=${productReferenceId}`;
    }

    return null;
  };

  const getCards = () => {
    if (!Array.isArray(products)) {
      return [];
    }
    return products.map((product, index) => {
      const {
        headline,
        attributes,
        price,
        initialPrice,
        image,
        salesforceId,
        productReferenceId,
        automatedOffer,
        sendAutomatedOffer,
        minLeadTime,
        rentalPriceBasis,
        rentalPrice,
        priceBasis,
        productGroupName,
        productGroupId,
        /* 
        The URL is available only for custom product tiles 
        and should direct to a custom product details page.
        */
        url,
      } = product;

      const subtitle = getSubtitle({
        initialPrice,
        price,
        priceBasis,
      });

      const description =
        attributes?.length && FEAT_FLAG_ATTRIBUTE_RANGES
          ? `<p>${attributes
              .map(
                ({ name, value, unit }) =>
                  `<span>${name}: ${formatValue(value, unit)}</span><br/>`
              )
              .join('')}</p>`
          : '';

      const onClick = event => {
        if (showContactForm) {
          return handleOpenContactForm({
            title: headline,
          });
        } else if (!salesforceId) {
          return;
        }

        return handleRentalRequestClick(event, {
          title: headline,
          productReferenceId,
          salesforceId,
          automatedOffer,
          sendAutomatedOffer,
          minLeadTime,
          rentalPriceBasis,
          rentalPrice,
          priceBasis,
          productGroupName,
          productGroupId,
        });
      };

      return {
        headline,
        subtitle,
        imageTag: initialPrice ? formatMessage('rental_price_sale') : null,
        image: typeof image === 'string' ? image : { data: image },
        description,
        'data-cy': `PriceCatalogTile ${index}`,
        onClick,

        cardAreaLink: getCardAreaLink({
          url,
          showContactForm,
          productReferenceId,
        }),
        link: url && !showContactForm ? url : null,
      };
    });
  };

  const cards = getCards();

  return (
    <>
      <CardGrid
        centerImage
        headingVariant="h5"
        showLinkText
        linkText={formatMessage('price_tile_CTA').toUpperCase()}
        cards={cards}
        data-cy={dataCy}
        loading={loading}
      />
      <AddToBasketConfirmationDialog
        open={!!duplicatedMachineData}
        onAddAgain={() => {
          handleAddToBasket(duplicatedMachineData);
        }}
        onDontAddAgain={() => {
          router.push('/basket');
        }}
        machineName={duplicatedMachineData?.rentalObject.name}
      />
      {showContactForm && (
        <ContactFormDialog
          open={contactFormOpen}
          defaultValues={contactFormData}
          onClose={handleCloseContactForm}
          onChange={updateContactFormFieldData}
          onEmailSent={resetContactFormData}
        />
      )}
    </>
  );
};

PriceCatalog.propTypes = {
  products: priceCatalogProductsPropType.isRequired,
  requestType: priceCatalogRequestTypePropType.isRequired,
  'data-cy': PropTypes.string,
  productGroupName: PropTypes.string,
  productGroupId: PropTypes.number,
  loading: PropTypes.bool,
};

PriceCatalog.defaultProps = {
  'data-cy': 'PriceCatalog',
  productGroupName: null,
  loading: false,
};

export default PriceCatalog;
